import { useMemo } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Badge,
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import { useAccountPaymentMethods } from "hooks/useAccountPaymentMethod"
import { useAccounts } from "hooks/useAccountEntity"
import { formatBDT } from "utils/formatPrice"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"

const extractCollection = value => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.data)) return value.data
  return []
}

const PAYMENT_MODE = "payment"
const REFUND_MODE = "refund"

const PaymentModal = ({
  isOpen,
  toggle,
  moneyReceipt,
  payment = null,
  onSubmit,
  isSubmitting = false,
  defaultEntryMode = PAYMENT_MODE,
}) => {
  const { data: paymentMethodResponse, isLoading: paymentMethodLoading } =
    useAccountPaymentMethods({ all: true, per_page: 200 }, isOpen)
  const { data: accountResponse, isLoading: accountLoading } =
    useAccounts({ all: true, per_page: 200 }, isOpen)

  const paymentMethods = extractCollection(paymentMethodResponse?.data)
  const accounts = extractCollection(accountResponse?.data)

  const paymentMethodOptions = useMemo(
    () => paymentMethods.map(item => ({ value: item.id, label: item.name })),
    [paymentMethods]
  )

  const accountOptions = useMemo(
    () => accounts.map(item => ({ value: item.id, label: item.name })),
    [accounts]
  )

  const isEdit = Boolean(payment?.id)
  const dueAmount = Number(moneyReceipt?.due_amount || 0)
  const amountPaid = Number(moneyReceipt?.amount_paid || 0)
  const existingAmount = Number(payment?.amount || 0)
  const initialEntryMode =
    existingAmount < 0 ? REFUND_MODE : defaultEntryMode || PAYMENT_MODE
  const currentNetPaid = amountPaid - existingAmount
  const maxReceivableAmount = Math.max(
    0,
    dueAmount + Math.max(0, existingAmount)
  )
  const maxRefundableAmount = Math.max(0, currentNetPaid)
  const today = new Date().toISOString().split("T")[0]

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      entry_mode: initialEntryMode,
      amount:
        Math.abs(existingAmount) ||
        (initialEntryMode === REFUND_MODE
          ? maxRefundableAmount || ""
          : ""),
      payment_method_id:
        payment?.payment_method_id ?? payment?.paymentMethod?.id ?? "",
      account_id: payment?.account_id ?? payment?.account?.id ?? "",
      received_date: payment?.received_date?.slice?.(0, 10) ?? today,
      reference_number: payment?.reference_number ?? "",
      notes: payment?.notes ?? "",
    },
    validationSchema: Yup.object({
      amount: Yup.number()
        .typeError("Amount must be numeric")
        .integer("Amount must be a whole number")
        .positive("Amount must be greater than zero")
        .test("within-limit", function (value) {
          const entryMode = this.parent?.entry_mode || PAYMENT_MODE
          const numericValue = Number(value || 0)

          if (!numericValue) {
            return this.createError({ message: "Amount is required" })
          }

          if (entryMode === REFUND_MODE && numericValue > maxRefundableAmount) {
            return this.createError({
              message: `Refund cannot exceed ${formatBDT(maxRefundableAmount)}.`,
            })
          }

          return true
        })
        .required("Amount is required"),
      entry_mode: Yup.string()
        .oneOf([PAYMENT_MODE, REFUND_MODE])
        .required("Transaction type is required"),
      payment_method_id: Yup.mixed().required("Payment method is required"),
      account_id: Yup.mixed().required("Account is required"),
      received_date: Yup.string().required("Received date is required"),
      reference_number: Yup.string().nullable().max(255, "Reference number is too long"),
      notes: Yup.string().when("entry_mode", {
        is: REFUND_MODE,
        then: schema => schema.required("Refund note is required"),
        otherwise: schema => schema.nullable(),
      }),
    }),
    onSubmit: async values => {
      const numericAmount = Number(values.amount)
      const signedAmount =
        values.entry_mode === REFUND_MODE
          ? -Math.abs(numericAmount)
          : Math.abs(numericAmount)

      await onSubmit({
        ...(isEdit ? { id: payment.id } : {}),
        money_receipt_id: moneyReceipt?.id,
        customer_id: moneyReceipt?.customer_id,
        vehicle_id: moneyReceipt?.vehicle_id,
        amount: signedAmount,
        payment_method_id: values.payment_method_id,
        account_id: values.account_id || null,
        received_date: values.received_date,
        reference_number: values.reference_number?.trim() || null,
        notes: values.notes?.trim() || null,
      })

      toggle()
    },
  })

  const entryMode = formik.values.entry_mode || PAYMENT_MODE
  const isRefundMode = entryMode === REFUND_MODE
  const modalTitle = isEdit
    ? isRefundMode
      ? "Edit Refund"
      : "Edit Payment"
    : isRefundMode
      ? "Add Refund"
      : "Add Payment"
  const submitLabel = isSubmitting
    ? "Saving..."
    : isEdit
      ? isRefundMode
        ? "Update Refund"
        : "Update Payment"
      : isRefundMode
        ? "Add Refund"
        : "Add Payment"
  const helperText = isRefundMode
    ? `Refund will debit the selected account. Maximum refundable amount: ${formatBDT(maxRefundableAmount)}.`
    : maxReceivableAmount > 0
      ? `Payment will credit the selected account. Maximum receivable amount: ${formatBDT(maxReceivableAmount)}.`
      : "This receipt is fully collected. Use refund mode to reverse money."

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <Form onSubmit={formik.handleSubmit}>
        <ModalHeader toggle={toggle}>{modalTitle}</ModalHeader>
        <ModalBody>
          <div className="border rounded bg-light p-3 mb-3">
            <div className="d-flex justify-content-between">
              <span>Total</span>
              <strong>{formatBDT(moneyReceipt?.total_amount)}</strong>
            </div>
            <div className="d-flex justify-content-between">
              <span>Paid</span>
              <strong>{formatBDT(moneyReceipt?.amount_paid)}</strong>
            </div>
            <div className="d-flex justify-content-between">
              <span>Due</span>
              <strong className="text-danger">{formatBDT(dueAmount)}</strong>
            </div>
            <div className="d-flex justify-content-between">
              <span>Overpaid</span>
              <strong className="text-warning">{formatBDT(moneyReceipt?.overpaid_amount)}</strong>
            </div>
          </div>

          <Row className="g-3">
            <Col md={12}>
              <Label className="form-label">Transaction Type</Label>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  type="button"
                  color={isRefundMode ? "light" : "primary"}
                  outline={isRefundMode}
                  onClick={() => formik.setFieldValue("entry_mode", PAYMENT_MODE)}
                >
                  Receive Payment
                </Button>
                <Button
                  type="button"
                  color={isRefundMode ? "danger" : "light"}
                  outline={!isRefundMode}
                  onClick={() => formik.setFieldValue("entry_mode", REFUND_MODE)}
                  disabled={maxRefundableAmount <= 0}
                >
                  Refund
                </Button>
                <Badge color={isRefundMode ? "danger" : "success"} className="align-self-center">
                  {isRefundMode ? "Money Out" : "Money In"}
                </Badge>
              </div>
              {formik.touched.entry_mode && formik.errors.entry_mode ? (
                <div className="text-danger small mt-1">{formik.errors.entry_mode}</div>
              ) : null}
            </Col>

            <Col md={12}>
              <Label className="form-label">
                {isRefundMode ? "Refund Amount" : "Payment Amount"}
              </Label>
              <Input
                type="number"
                name="amount"
                value={formik.values.amount}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.amount && formik.errors.amount)}
              />
              <FormFeedback>{formik.errors.amount}</FormFeedback>
              <div className="small text-muted mt-1">
                {helperText}
              </div>
            </Col>

            <Col md={12}>
              <Label className="form-label">Received Date</Label>
              <Input
                type="date"
                name="received_date"
                value={formik.values.received_date}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.received_date && formik.errors.received_date)}
              />
              <FormFeedback>{formik.errors.received_date}</FormFeedback>
            </Col>

            <Col md={12}>
              <FormikCreatableSelect
                name="payment_method_id"
                formik={formik}
                label="Payment Method"
                options={paymentMethodOptions}
                loading={paymentMethodLoading}
                required
              />
            </Col>

            <Col md={12}>
              <FormikCreatableSelect
                name="account_id"
                formik={formik}
                label="Account"
                options={accountOptions}
                loading={accountLoading}
                required
              />
            </Col>

            <Col md={12}>
              <Label className="form-label">Reference Number</Label>
              <Input
                type="text"
                name="reference_number"
                value={formik.values.reference_number}
                onChange={formik.handleChange}
                invalid={
                  !!(formik.touched.reference_number && formik.errors.reference_number)
                }
              />
              <FormFeedback>{formik.errors.reference_number}</FormFeedback>
            </Col>

            <Col md={12}>
              <Label className="form-label">Notes</Label>
              <Input
                type="textarea"
                name="notes"
                rows={3}
                value={formik.values.notes}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.notes && formik.errors.notes)}
              />
              <FormFeedback>{formik.errors.notes}</FormFeedback>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggle} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isSubmitting}>
            {submitLabel}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default PaymentModal
